import { CommonModule } from '@angular/common';
import { Component, computed, effect, ElementRef, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CustomerUpdateRequest } from '../../../../../../core/models/customer.model';
import { ClientSettingsService } from '../../../../../../core/services/client-settings.service';
import { CustomerService } from '../../../../../../core/services/customer.service';
import { Modal } from '../../../../../../shared/components/modal/modal';
import { ConfirmationService } from '../../../../../../shared';

interface CustomerSnapshot {
  email: string;
  cnpj: string;
  telefone: string;
  isActive: boolean;
}

interface CustomerLogoSubmitResult {
  success: boolean;
  error?: string;
  nameUpdated: boolean;
  logoUpdated: boolean;
  removed: boolean;
  settings?: unknown;
}

@Component({
  selector: 'app-customer-logo-modal',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, Modal],
  templateUrl: './customer-logo-modal.html',
  styleUrl: './customer-logo-modal.scss',
})
export class CustomerLogoModal implements OnDestroy {
  private readonly activeModal = inject(NgbActiveModal);

  private readonly clientSettingsService = inject(ClientSettingsService);

  private readonly customerService = inject(CustomerService);

  private readonly transloco = inject(TranslocoService);

  private readonly confirmationService = inject(ConfirmationService);

  @ViewChild('fileInput')
  readonly fileInput?: ElementRef<HTMLInputElement>;

  /**
   * Recebido pelo modal principal usando:
   *
   * modalRef.componentInstance.customerId.set(id);
   */
  readonly customerId = signal('');

  /**
   * Nome editável da empresa.
   */
  readonly customerName = signal('');

  /**
   * Nome carregado originalmente.
   * Usado para detectar alterações.
   */
  readonly originalCustomerName = signal('');

  readonly errorMessage = signal('');

  readonly selectedFile = signal<File | null>(null);

  readonly currentLogoUrl = signal<string | null>(null);

  readonly previewUrl = signal<string | null>(null);

  readonly removeRequested = signal(false);

  readonly isDragging = signal(false);

  /**
   * Controla operações que não pertencem diretamente
   * ao estado de carregamento do ClientSettingsService.
   */
  private readonly localLoading = signal(false);

  readonly isLoading = computed(() => {
    return this.localLoading() || this.clientSettingsService.isLoading();
  });

  private loadedCustomerId = '';

  private localObjectUrl: string | null = null;

  /**
   * Dados que precisam ser preservados ao atualizar
   * somente o nome da empresa.
   */
  private customerSnapshot: CustomerSnapshot | null = null;

  private readonly maximumFileSize = 5 * 1024 * 1024;

  private readonly allowedFileTypes = new Set<string>(['image/png', 'image/jpeg', 'image/webp']);

  readonly displayName = computed(() => {
    return this.customerName().trim() || this.transloco.translate('admin.customers.edit.logo.companyNameFallback');
  });

  readonly hasCompanyNameChanged = computed(() => {
    return this.customerName().trim() !== this.originalCustomerName().trim();
  });

  readonly hasChanges = computed(() => {
    return this.selectedFile() !== null || this.removeRequested() || this.hasCompanyNameChanged();
  });

  readonly canSubmit = computed(() => {
    return !this.isLoading() && Boolean(this.customerName().trim()) && this.hasChanges();
  });

  readonly selectedFileSize = computed(() => {
    const file = this.selectedFile();

    if (!file) {
      return '';
    }

    return this.formatFileSize(file.size);
  });

  constructor() {
    effect(() => {
      const clientId = this.customerId().trim();

      if (!clientId || clientId === this.loadedCustomerId) {
        return;
      }

      this.loadedCustomerId = clientId;

      this.loadData(clientId);
    });
  }

  ngOnDestroy(): void {
    this.clearLocalObjectUrl();
  }

  /**
   * Carrega simultaneamente os dados da empresa
   * e as configurações que contêm a logo.
   */
  private async loadData(clientId: string): Promise<void> {
    this.errorMessage.set('');
    this.localLoading.set(true);

    try {
      const [customer, settings] = await Promise.all([
        this.customerService.getCustomerById(clientId),

        this.clientSettingsService.getByClientId(clientId),
      ]);

      if (!customer) {
        this.loadedCustomerId = '';

        this.errorMessage.set(this.transloco.translate('admin.customers.edit.errors.loadCompanyData'));

        return;
      }

      if (!settings) {
        this.loadedCustomerId = '';

        this.errorMessage.set(this.transloco.translate('admin.customers.edit.logo.errors.loadCurrentLogo'));

        return;
      }

      const normalizedName = customer.nome?.trim() ?? '';

      this.customerName.set(normalizedName);

      this.originalCustomerName.set(normalizedName);

      this.customerSnapshot = {
        email: customer.email?.trim() ?? '',

        cnpj: customer.cnpj?.trim() ?? '',

        telefone: customer.telefone?.trim() ?? '',

        isActive: customer.isActive ?? true,
      };

      const logoUrl = this.addCacheBuster(settings.logoUrl ?? null);

      this.currentLogoUrl.set(logoUrl);

      this.previewUrl.set(logoUrl);

      this.removeRequested.set(false);
      this.selectedFile.set(null);
    } catch {
      this.loadedCustomerId = '';

      this.errorMessage.set(this.transloco.translate('admin.customers.edit.logo.errors.loadCompanyInfo'));
    } finally {
      this.localLoading.set(false);
    }
  }

  onCompanyNameChange(event: Event): void {
    const input = event.target as HTMLInputElement;

    this.customerName.set(input.value);

    this.errorMessage.set('');
  }

  openFilePicker(): void {
    if (this.isLoading()) {
      return;
    }

    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    const file = input.files?.item(0);

    if (file) {
      this.selectFile(file);
    }

    /**
     * Permite selecionar novamente o mesmo arquivo.
     */
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();

    if (this.isLoading()) {
      return;
    }

    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();

    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();

    this.isDragging.set(false);

    if (this.isLoading()) {
      return;
    }

    const file = event.dataTransfer?.files.item(0);

    if (file) {
      this.selectFile(file);
    }
  }

  private selectFile(file: File): void {
    this.errorMessage.set('');

    if (!this.allowedFileTypes.has(file.type)) {
      this.errorMessage.set(this.transloco.translate('admin.customers.edit.logo.errors.invalidFormat'));

      return;
    }

    if (file.size > this.maximumFileSize) {
      this.errorMessage.set(this.transloco.translate('admin.customers.edit.logo.errors.maxFileSize'));

      return;
    }

    if (file.size === 0) {
      this.errorMessage.set(this.transloco.translate('admin.customers.edit.logo.errors.emptyFile'));

      return;
    }

    this.clearLocalObjectUrl();

    this.localObjectUrl = URL.createObjectURL(file);

    this.selectedFile.set(file);

    this.previewUrl.set(this.localObjectUrl);

    this.removeRequested.set(false);
  }

  removeLogo(): void {
    this.clearLocalObjectUrl();

    this.selectedFile.set(null);
    this.previewUrl.set(null);
    this.removeRequested.set(true);
    this.errorMessage.set('');
  }

  restoreLogo(): void {
    this.clearLocalObjectUrl();

    this.selectedFile.set(null);

    this.previewUrl.set(this.currentLogoUrl());

    this.removeRequested.set(false);
    this.errorMessage.set('');
  }

  onPreviewError(): void {
    this.errorMessage.set(this.transloco.translate('admin.customers.edit.logo.errors.preview'));
  }

  async onSubmit(): Promise<void> {
    this.errorMessage.set('');

    const clientId = this.customerId().trim();
    const companyName = this.customerName().trim();

    if (!this.validateSubmit(clientId, companyName)) {
      return;
    }

    this.localLoading.set(true);

    let companyNameUpdated = false;
    // Sempre atribuído dentro do try antes de ser lido; os caminhos que não atribuem retornam.
    let result!: CustomerLogoSubmitResult;

    try {
      if (this.hasCompanyNameChanged()) {
        const nameUpdated = await this.updateCompanyName(clientId, companyName);

        if (!nameUpdated) {
          return;
        }

        companyNameUpdated = true;

        /*
         * Evita reenviar a alteração do nome caso a alteração
         * da logo falhe e o usuário tente novamente.
         */
        this.originalCustomerName.set(companyName);
      }

      result = await this.buildSubmitResult(clientId, companyNameUpdated);

      if (!result.success) {
        this.errorMessage.set(result.error ?? this.getFallbackErrorMessageForSaveChanges());

        return;
      }
    } catch {
      this.errorMessage.set(
        companyNameUpdated
          ? this.getFallbackErrorMessageAfterNameUpdate()
          : this.getFallbackErrorMessageForSaveChanges(),
      );

      return;
    } finally {
      this.localLoading.set(false);
    }

    this.activeModal.close({
      updated: true,
      nameUpdated: result.nameUpdated,
      logoUpdated: result.logoUpdated,
      removed: result.removed,
      customerId: clientId,
      customerName: companyName,
      settings: result.settings,
    });

    await new Promise((resolve) => setTimeout(resolve, 200));

    await this.confirmationService.showInfo(
      this.transloco.translate('confirmation.logoSaved.title'),
      this.transloco.translate('confirmation.logoSaved.message'),
      'success',
    );
  }

  /**
   * Resolve o que fazer com a logo: enviar, remover, ou apenas confirmar a troca de nome.
   * Extraído do onSubmit para manter a complexidade daquele método sob controle.
   */
  private async buildSubmitResult(clientId: string, companyNameUpdated: boolean): Promise<CustomerLogoSubmitResult> {
    const file = this.selectedFile();

    if (file) {
      const logoResult = await this.clientSettingsService.updateClientLogo(clientId, file);

      return {
        success: logoResult.success,
        error:
          logoResult.error ??
          (companyNameUpdated
            ? this.transloco.translate('admin.customers.edit.logo.errors.uploadAfterNameUpdate')
            : this.transloco.translate('admin.customers.edit.logo.errors.upload')),
        nameUpdated: companyNameUpdated,
        logoUpdated: logoResult.success,
        removed: false,
        settings: logoResult.data,
      };
    }

    if (this.removeRequested()) {
      const logoResult = await this.clientSettingsService.removeClientLogo(clientId);

      return {
        success: logoResult.success,
        error:
          logoResult.error ??
          (companyNameUpdated
            ? this.transloco.translate('admin.customers.edit.logo.errors.removeAfterNameUpdate')
            : this.transloco.translate('admin.customers.edit.logo.errors.remove')),
        nameUpdated: companyNameUpdated,
        logoUpdated: false,
        removed: logoResult.success,
        settings: logoResult.data,
      };
    }

    if (companyNameUpdated) {
      return {
        success: true,
        nameUpdated: true,
        logoUpdated: false,
        removed: false,
      };
    }

    return {
      success: false,
      error: this.transloco.translate('admin.customers.edit.logo.errors.noChangeSelection'),
      nameUpdated: false,
      logoUpdated: false,
      removed: false,
    };
  }

  private validateSubmit(clientId: string, companyName: string): boolean {
    if (!clientId) {
      this.errorMessage.set(this.transloco.translate('admin.customers.edit.errors.identifyClient'));

      return false;
    }

    if (!companyName) {
      this.errorMessage.set(this.transloco.translate('admin.customers.edit.logo.companyNameRequired'));

      return false;
    }

    if (!this.hasChanges()) {
      this.errorMessage.set(this.transloco.translate('admin.customers.edit.logo.errors.noChanges'));

      return false;
    }

    return true;
  }

  private getFallbackErrorMessageAfterNameUpdate(): string {
    return this.transloco.translate('admin.customers.edit.logo.errors.processAfterNameUpdate');
  }

  private getFallbackErrorMessageForSaveChanges(): string {
    return this.transloco.translate('admin.customers.edit.logo.errors.saveChanges');
  }

  private async updateCompanyName(clientId: string, companyName: string): Promise<boolean> {
    if (!this.customerSnapshot) {
      this.errorMessage.set(this.transloco.translate('admin.customers.edit.logo.errors.currentCompanyData'));

      return false;
    }

    const request: CustomerUpdateRequest = {
      nome: companyName,

      email: this.customerSnapshot.email,

      cnpj: this.customerSnapshot.cnpj,

      telefone: this.customerSnapshot.telefone,

      isActive: this.customerSnapshot.isActive,
    };

    const result = await this.customerService.updateCustomer(clientId, request);

    if (!result.success) {
      this.errorMessage.set(
        result.error ?? this.transloco.translate('admin.customers.edit.logo.errors.updateCompanyName'),
      );

      return false;
    }

    return true;
  }

  /**
   * Adiciona uma versão à URL sempre que o modal
   * carrega a logo, forçando o navegador a buscar
   * a imagem mais recente.
   */
  private addCacheBuster(logoUrl: string | null): string | null {
    const normalizedLogoUrl = logoUrl?.trim() ?? '';

    if (!normalizedLogoUrl) {
      return null;
    }

    try {
      const url = new URL(normalizedLogoUrl);

      url.searchParams.set('v', Date.now().toString());

      return url.toString();
    } catch {
      const separator = normalizedLogoUrl.includes('?') ? '&' : '?';

      return `${normalizedLogoUrl}${separator}v=${Date.now()}`;
    }
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  private clearLocalObjectUrl(): void {
    if (!this.localObjectUrl) {
      return;
    }

    URL.revokeObjectURL(this.localObjectUrl);

    this.localObjectUrl = null;
  }

  private formatFileSize(size: number): string {
    if (size < 1024) {
      return `${size} B`;
    }

    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
}
